import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Code2, Play, ChevronLeft, ChevronRight, CheckCircle, 
  Sun, Moon, ArrowLeft, BookOpen, Terminal, Lightbulb, 
  RotateCcw, User, LogOut, Award, Image as ImageIcon,
  Download, Globe, Monitor, CheckSquare, Square, ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChapterCharacter } from '../components/ChapterCharacter';
import { coursesApi, executeApi, progressApi, courseRequestsApi, codeSnapshotApi } from '../lib/api';
import { toast } from 'sonner';
import { TeacherAvatar } from '../components/TeacherAvatar';
import Editor from '@monaco-editor/react';
import { Bug, PanelLeftClose, PanelLeftOpen, FileText, FileCode, Rows, Columns, LayoutPanelLeft, StepForward, StepBack, SkipForward, History } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import AnimatedConceptExplainer from '../components/AnimatedConceptExplainer';
import { Sparkles } from 'lucide-react';

// Map our language codes → Monaco language ids (for syntax highlight + IntelliSense).
const MONACO_LANG_MAP = {
  python: 'python', java: 'java', c: 'c', cpp: 'cpp', sql: 'sql',
  html: 'html', php: 'php', bash: 'shell', shell: 'shell', powershell: 'powershell',
  javascript: 'javascript', go: 'go', perl: 'perl',
  mobile: 'javascript', game2d: 'javascript', game3d: 'python',
  lua: 'lua', mobileui: 'dart',
};

// Course-language → file extension shown in the tab bar (VS Code style).
const PISTON_EXT = {
  python: 'py', java: 'java', c: 'c', cpp: 'cpp', sql: 'sql',
  html: 'html', php: 'php', bash: 'sh', shell: 'sh', powershell: 'ps1',
  javascript: 'js', go: 'go', perl: 'pl',
  mobile: 'jsx', game2d: 'js', game3d: 'gd',
  lua: 'lua', mobileui: 'dart',
};

// Supported Indian Languages
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ' },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া' },
];

// UI translations for interface
const UI_TRANSLATIONS = {
  en: { chapters: 'Chapters', runCode: 'Run Code', reset: 'Reset', markComplete: 'Mark as Complete', previous: 'Previous', next: 'Next', takeExam: 'Take Final Exam', setupGuide: 'Setup Guide', checklist: 'Installation Checklist', videoTutorial: 'Video Tutorial' },
  hi: { chapters: 'अध्याय', runCode: 'कोड चलाएं', reset: 'रीसेट', markComplete: 'पूर्ण के रूप में चिह्नित करें', previous: 'पिछला', next: 'अगला', takeExam: 'अंतिम परीक्षा दें', setupGuide: 'सेटअप गाइड', checklist: 'इंस्टॉलेशन चेकलिस्ट', videoTutorial: 'वीडियो ट्यूटोरियल' },
  ta: { chapters: 'அத்தியாயங்கள்', runCode: 'குறியீட்டை இயக்கு', reset: 'மீட்டமை', markComplete: 'முடிந்ததாக குறி', previous: 'முந்தைய', next: 'அடுத்து', takeExam: 'இறுதி தேர்வு எழுது', setupGuide: 'அமைப்பு வழிகாட்டி', checklist: 'நிறுவல் பட்டியல்', videoTutorial: 'வீடியோ பயிற்சி' },
  te: { chapters: 'అధ్యాయాలు', runCode: 'కోడ్ రన్ చేయండి', reset: 'రీసెట్', markComplete: 'పూర్తి అని గుర్తించండి', previous: 'మునుపటి', next: 'తదుపరి', takeExam: 'ఫైనల్ పరీక్ష రాయండి', setupGuide: 'సెటప్ గైడ్', checklist: 'ఇన్‌స్టాలేషన్ చెక్‌లిస్ట్', videoTutorial: 'వీడియో ట్యుటోరియల్' },
  kn: { chapters: 'ಅಧ್ಯಾಯಗಳು', runCode: 'ಕೋಡ್ ರನ್ ಮಾಡಿ', reset: 'ಮರುಹೊಂದಿಸಿ', markComplete: 'ಪೂರ್ಣ ಎಂದು ಗುರುತಿಸಿ', previous: 'ಹಿಂದಿನ', next: 'ಮುಂದಿನ', takeExam: 'ಅಂತಿಮ ಪರೀಕ್ಷೆ ತೆಗೆದುಕೊಳ್ಳಿ', setupGuide: 'ಸೆಟಪ್ ಗೈಡ್', checklist: 'ಇನ್‌ಸ್ಟಾಲೇಶನ್ ಚೆಕ್‌ಲಿಸ್ಟ್', videoTutorial: 'ವೀಡಿಯೊ ಟ್ಯುಟೋರಿಯಲ್' },
  ml: { chapters: 'അധ്യായങ്ങൾ', runCode: 'കോഡ് റൺ ചെയ്യുക', reset: 'റീസെറ്റ്', markComplete: 'പൂർത്തിയായതായി അടയാളപ്പെടുത്തുക', previous: 'മുമ്പത്തെ', next: 'അടുത്തത്', takeExam: 'ഫൈനൽ പരീക്ഷ എഴുതുക', setupGuide: 'സെറ്റപ്പ് ഗൈഡ്', checklist: 'ഇൻസ്റ്റലേഷൻ ചെക്ക്‌ലിസ്റ്റ്', videoTutorial: 'വീഡിയോ ട്യൂട്ടോറിയൽ' },
  bn: { chapters: 'অধ্যায়', runCode: 'কোড চালান', reset: 'রিসেট', markComplete: 'সম্পূর্ণ হিসাবে চিহ্নিত করুন', previous: 'পূর্ববর্তী', next: 'পরবর্তী', takeExam: 'ফাইনাল পরীক্ষা দিন', setupGuide: 'সেটআপ গাইড', checklist: 'ইনস্টলেশন চেকলিস্ট', videoTutorial: 'ভিডিও টিউটোরিয়াল' },
  mr: { chapters: 'अध्याय', runCode: 'कोड चालवा', reset: 'रीसेट', markComplete: 'पूर्ण म्हणून चिन्हांकित करा', previous: 'मागील', next: 'पुढील', takeExam: 'अंतिम परीक्षा द्या', setupGuide: 'सेटअप मार्गदर्शक', checklist: 'इंस्टॉलेशन चेकलिस्ट', videoTutorial: 'व्हिडिओ ट्यूटोरियल' },
  gu: { chapters: 'પ્રકરણો', runCode: 'કોડ ચલાવો', reset: 'રીસેટ', markComplete: 'પૂર્ણ તરીકે ચિહ્નિત કરો', previous: 'પાછલું', next: 'આગળ', takeExam: 'ફાઇનલ પરીક્ષા આપો', setupGuide: 'સેટઅપ માર્ગદર્શિકા', checklist: 'ઇન્સ્ટોલેશન ચેકલિસ્ટ', videoTutorial: 'વિડિઓ ટ્યુટોરિયલ' },
  pa: { chapters: 'ਅਧਿਆਏ', runCode: 'ਕੋਡ ਚਲਾਓ', reset: 'ਰੀਸੈਟ', markComplete: 'ਪੂਰਾ ਵਜੋਂ ਚਿੰਨ੍ਹਿਤ ਕਰੋ', previous: 'ਪਿਛਲਾ', next: 'ਅਗਲਾ', takeExam: 'ਫਾਈਨਲ ਪ੍ਰੀਖਿਆ ਦਿਓ', setupGuide: 'ਸੈੱਟਅੱਪ ਗਾਈਡ', checklist: 'ਇੰਸਟਾਲੇਸ਼ਨ ਚੈਕਲਿਸਟ', videoTutorial: 'ਵੀਡੀਓ ਟਿਊਟੋਰੀਅਲ' },
  or: { chapters: 'ଅଧ୍ୟାୟ', runCode: 'କୋଡ୍ ଚଲାନ୍ତୁ', reset: 'ରିସେଟ୍', markComplete: 'ସମ୍ପୂର୍ଣ୍ଣ ବୋଲି ଚିହ୍ନିତ କରନ୍ତୁ', previous: 'ପୂର୍ବ', next: 'ପରବର୍ତ୍ତୀ', takeExam: 'ଫାଇନାଲ୍ ପରୀକ୍ଷା ଦିଅନ୍ତୁ', setupGuide: 'ସେଟଅପ୍ ଗାଇଡ୍', checklist: 'ଇନଷ୍ଟଲେସନ୍ ଚେକଲିଷ୍ଟ', videoTutorial: 'ଭିଡିଓ ଟ୍ୟୁଟୋରିଆଲ' },
  as: { chapters: 'অধ্যায়', runCode: 'ক\'ড চলাওক', reset: 'ৰিছেট', markComplete: 'সম্পূৰ্ণ বুলি চিহ্নিত কৰক', previous: 'পূৰ্বৱৰ্তী', next: 'পৰৱৰ্তী', takeExam: 'চূড়ান্ত পৰীক্ষা দিয়ক', setupGuide: 'ছেটআপ গাইড', checklist: 'ইনষ্টলেশ্বন চেকলিষ্ট', videoTutorial: 'ভিডিঅ\' টিউট\'ৰিয়েল' },
};

// Setup checklist items for each language
const SETUP_CHECKLIST = {
  python: [
    { id: 1, text: 'Download Python from python.org', link: 'https://www.python.org/downloads/' },
    { id: 2, text: 'Run the installer (check "Add Python to PATH")', link: null },
    { id: 3, text: 'Open terminal/command prompt', link: null },
    { id: 4, text: 'Verify with: python --version', link: null },
    { id: 5, text: 'Install VS Code editor', link: 'https://code.visualstudio.com/' },
    { id: 6, text: 'Install Python extension in VS Code', link: null },
  ],
  java: [
    { id: 1, text: 'Download JDK from Oracle', link: 'https://www.oracle.com/java/technologies/downloads/' },
    { id: 2, text: 'Run the installer', link: null },
    { id: 3, text: 'Set JAVA_HOME environment variable', link: null },
    { id: 4, text: 'Verify with: java --version', link: null },
    { id: 5, text: 'Install VS Code or IntelliJ IDEA', link: 'https://code.visualstudio.com/' },
    { id: 6, text: 'Install Java Extension Pack', link: null },
  ],
  c: [
    { id: 1, text: 'Windows: Install MinGW-w64', link: 'https://www.mingw-w64.org/' },
    { id: 2, text: 'Mac: Install Xcode Command Line Tools', link: null },
    { id: 3, text: 'Linux: sudo apt install gcc', link: null },
    { id: 4, text: 'Verify with: gcc --version', link: null },
    { id: 5, text: 'Install VS Code editor', link: 'https://code.visualstudio.com/' },
    { id: 6, text: 'Install C/C++ extension', link: null },
  ],
  // ... other languages use similar pattern
};

// Category-based images and icons
const categoryVisuals = {
  life_skills: {
    gradient: 'from-green-500 to-emerald-600',
    icon: '🎯',
    images: [
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
      'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800',
      'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800',
    ]
  },
  safety: {
    gradient: 'from-red-500 to-orange-600',
    icon: '🛡️',
    images: [
      'https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?w=800',
      'https://images.unsplash.com/photo-1557862921-37829c790f19?w=800',
      'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800',
    ]
  },
  financial: {
    gradient: 'from-yellow-500 to-amber-600',
    icon: '💰',
    images: [
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
      'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800',
      'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800',
    ]
  },
  career: {
    gradient: 'from-purple-500 to-indigo-600',
    icon: '🚀',
    images: [
      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800',
      'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800',
      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800',
    ]
  },
  environment: {
    gradient: 'from-teal-500 to-cyan-600',
    icon: '🌱',
    images: [
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800',
      'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800',
      'https://images.unsplash.com/photo-1518173946687-a4c036bc1e33?w=800',
    ]
  },
  competitive_exams: {
    gradient: 'from-amber-500 to-orange-600',
    icon: '🏆',
    images: [
      'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800',
      'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800',
      'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800',
    ]
  }
};

export default function CodeLab() {
  const { courseId } = useParams();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [myCourses, setMyCourses] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ completed_chapters: [] });
  const [showAvatar, setShowAvatar] = useState(true);
  const [avatarDismissed, setAvatarDismissed] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(courseId);
  const [chapterImage, setChapterImage] = useState(null);
  const [breakpoints, setBreakpoints] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('chapter'); // 'chapter' | 'code'
  const [layoutMode, setLayoutMode] = useState('tabs'); // 'tabs' | 'vsplit' | 'hsplit'
  const [outputWidth, setOutputWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('codelab_output_w') || '', 10);
    return Number.isFinite(v) && v >= 200 && v <= 900 ? v : 320;
  });
  const startResizeOutput = React.useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = outputWidth;
    const onMove = (ev) => {
      const dx = startX - ev.clientX;
      const next = Math.min(900, Math.max(200, startW + dx));
      setOutputWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      try { localStorage.setItem('codelab_output_w', String(outputWidth)); } catch {}
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [outputWidth]);
  // Persist width whenever it settles.
  React.useEffect(() => {
    try { localStorage.setItem('codelab_output_w', String(outputWidth)); } catch {}
  }, [outputWidth]);
  const [debugTrace, setDebugTrace] = useState({}); // {line_no: {var: value_str, ...}}
  const [stepIdx, setStepIdx] = useState(-1); // index into breakpoints_hit for step controls
  const [debugSteps, setDebugSteps] = useState([]); // ordered array of line numbers the debugger paused on
  const editorRef = React.useRef(null);
  const monacoRef = React.useRef(null);
  const hoverProviderRef = React.useRef(null);
  // Phase 40.17 — Per-user, per-chapter code snapshot (auto-saved).
  const codeSaveTimerRef = React.useRef(null);
  const [snapshotStatus, setSnapshotStatus] = useState('idle'); // 'saving' | 'saved' | 'idle'
  // Phase 40.18 — Track the last persisted code so we can detect unsaved
  // edits and prompt the student before we swap chapters.
  const [savedCode, setSavedCode] = useState('');
  // Phase 40.19 — Web-preview support: HTML/CSS courses render a live
  // sandboxed iframe instead of piping code through the executor.
  const [previewSrc, setPreviewSrc] = useState('');
  const isWebPreviewLanguage = React.useMemo(() => {
    const lang = String(course?.language || '').toLowerCase();
    return lang === 'html' || lang === 'css' || lang === 'htmlcss' || lang === 'html/css';
  }, [course]);
  // Phase B — React Playground: the dedicated React course uses an iframe
  // that runs Babel Standalone + React 18 UMD so students see their JSX
  // rendered as real components (not just Node console output).
  const isReactPlayground = React.useMemo(() => {
    return course?.course_id === 'course_react_v1';
  }, [course]);

  // Phase 40.19d — last-5 versions ring buffer surfaced via a History dropdown.
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Prevents the autosave from marking code as "dirty" when we programmatically
  // reset it during a chapter switch.
  const suppressAutosaveRef = React.useRef(false);
  
  // New state for language and setup checklist
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });
  const [setupChecklist, setSetupChecklist] = useState(() => {
    const saved = localStorage.getItem(`setupChecklist_${courseId}`);
    return saved ? JSON.parse(saved) : {};
  });
  // Translation cache: key = `${courseId}_${order}_${lang}` → translated content
  const [translationCache, setTranslationCache] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Get UI text in selected language
  const t = UI_TRANSLATIONS[selectedLanguage] || UI_TRANSLATIONS.en;
  
  // Check if current chapter is setup chapter (chapter 1)
  const isSetupChapter = currentChapterIndex === 0 && course?.has_code_lab !== false;
  
  // Get checklist for current language
  const currentChecklist = SETUP_CHECKLIST[course?.language] || SETUP_CHECKLIST.python;

  // Translate current chapter on demand
  const translateChapter = async (cId, order, lang, englishContent) => {
    if (lang === 'en') return englishContent;
    const cacheKey = `${cId}_${order}_${lang}`;
    if (translationCache[cacheKey]) return translationCache[cacheKey];
    
    setIsTranslating(true);
    try {
      const result = await coursesApi.translateChapter(cId, order, lang);
      const translatedContent = result.content;
      setTranslationCache(prev => ({ ...prev, [cacheKey]: translatedContent }));
      return translatedContent;
    } catch (err) {
      console.warn('Translation failed, showing English:', err);
      return englishContent;
    } finally {
      setIsTranslating(false);
    }
  };

  // Save language preference and translate CURRENT chapter immediately
  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    if (course?.has_code_lab === false && currentChapter && selectedCourse) {
      const order = (currentChapterIndex || 0) + 1;
      const englishContent = currentChapter.content;
      const translated = await translateChapter(selectedCourse, order, langCode, englishContent);
      setCurrentChapter(prev => ({ ...prev, content: translated }));
    }
  };

  // Toggle checklist item
  const toggleChecklistItem = (itemId) => {
    const newChecklist = { ...setupChecklist, [itemId]: !setupChecklist[itemId] };
    setSetupChecklist(newChecklist);
    localStorage.setItem(`setupChecklist_${courseId}`, JSON.stringify(newChecklist));
  };

  const fetchCourseData = useCallback(async (cId, langOverride = null) => {
    try {
      setLoading(true);
      const courseData = await coursesApi.getById(cId);

      // Phase 40.19 — Non-programming courses render as MissionRunner scenes
      // (character + speech-bubble + wisdom), NOT the IDE-style Chapter.md
      // reader. Redirect immediately.
      if (courseData?.has_code_lab === false) {
        navigate(`/mission/${cId}`, { replace: true });
        return;
      }

      setCourse(courseData);
      
      let chaptersData = courseData.chapters_data || [];
      
      // For non-programming courses, fetch English chapters (structure + content)
      if (courseData.has_code_lab === false) {
        try {
          const englishChapters = await coursesApi.getChaptersTranslated(cId, 'en');
          if (englishChapters && englishChapters.length > 0) {
            chaptersData = englishChapters;
            courseData.chapters_data = englishChapters;
          }
        } catch (err) {
          console.warn('Failed to fetch chapter structure, using default:', err);
        }
      }
      
      if (chaptersData.length > 0) {
        const progressData = await progressApi.get(cId);
        setProgress(progressData);
        
        // Find current chapter
        let chapterIdx = 0;
        if (progressData.current_chapter_id) {
          const idx = chaptersData.findIndex(
            ch => ch.chapter_id === progressData.current_chapter_id
          );
          if (idx >= 0) chapterIdx = idx;
        }
        
        setCurrentChapterIndex(chapterIdx);
        const chapter = chaptersData[chapterIdx];
        // Phase 40.18 — Also try to hydrate the initial chapter from the
        // student's saved snapshot so refresh-page-and-resume works.
        const initialStarter = chapter.starter_code || chapter.code_template || _defaultStarter(courseData.language);
        setCode(initialStarter);
        setSavedCode(initialStarter);
        try {
          const snap = await codeSnapshotApi.get(courseData.id, chapterIdx);
          if (snap && typeof snap.code === 'string' && snap.code.length > 0) {
            suppressAutosaveRef.current = true;
            setCode(snap.code);
            setSavedCode(snap.code);
            setSnapshotStatus('saved');
            setTimeout(() => { suppressAutosaveRef.current = false; }, 50);
          }
        } catch { /* keep starter */ }
        
        // Set image for non-programming courses
        if (!courseData.has_code_lab) {
          const visuals = categoryVisuals[courseData.category] || categoryVisuals.life_skills;
          setChapterImage(visuals.images[chapterIdx % visuals.images.length]);
          
          // Apply translation to initial chapter if language is non-English
          const langToUse = langOverride || selectedLanguage;
          if (langToUse !== 'en') {
            setCurrentChapter(chapter); // Show English immediately
            const order = chapterIdx + 1;
            const translated = await translateChapter(cId, order, langToUse, chapter.content);
            setCurrentChapter(prev => ({ ...prev, content: translated }));
          } else {
            setCurrentChapter(chapter);
          }
        } else {
          setCurrentChapter(chapter);
        }
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myCoursesData = await courseRequestsApi.getMyCourses();
        setMyCourses(myCoursesData);
        
        if (selectedCourse) {
          await fetchCourseData(selectedCourse);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    
    fetchData();
  }, [selectedCourse, fetchCourseData]);

  const handleCourseChange = (newCourseId) => {
    setSelectedCourse(newCourseId);
    navigate(`/lab/${newCourseId}`, { replace: true });
  };

  // ---------------------------------------------------------------------
  // Phase 40.18 — starter-code fallback per language, used when a chapter
  // is missing `starter_code` / `code_template` (which was causing the
  // "Mini Project / Python / C++" chapters to keep the previous chapter's
  // code onscreen).  A tiny "hello world" is always more informative than
  // an empty editor OR stale code from another chapter.
  // ---------------------------------------------------------------------
  // Shared Monaco onChange handler used by all three <Editor> renders so
  // autosave, dirty-tracking, and chapter switching stay in sync everywhere.
  const handleCodeChange = React.useCallback((v) => {
    const next = v || '';
    setCode(next);
    if (suppressAutosaveRef.current) return;
    if (codeSaveTimerRef.current) clearTimeout(codeSaveTimerRef.current);
    setSnapshotStatus('saving');
    const chIdx = currentChapterIndex;
    const courseIdSnapshot = selectedCourse;
    codeSaveTimerRef.current = setTimeout(() => {
      codeSnapshotApi
        .save(courseIdSnapshot, chIdx, next)
        .then(() => {
          setSavedCode(next);
          setSnapshotStatus('saved');
        })
        .catch(() => setSnapshotStatus('idle'));
    }, 800);
  }, [currentChapterIndex, selectedCourse]);

  const _defaultStarter = (lang) => {
    switch ((lang || '').toLowerCase()) {
      case 'python': return '# Write your Python code here\nprint("Hello, world!")\n';
      case 'javascript':
      case 'mobile':
      case 'game2d': return '// Write your JavaScript code here\nconsole.log("Hello, world!");\n';
      case 'c': return '#include <stdio.h>\n\nint main() {\n    printf("Hello, world!\\n");\n    return 0;\n}\n';
      case 'cpp': return '#include <iostream>\n\nint main() {\n    std::cout << "Hello, world!" << std::endl;\n    return 0;\n}\n';
      case 'java': return 'public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, world!");\n    }\n}\n';
      case 'ruby': return '# Write your Ruby code here\nputs "Hello, world!"\n';
      case 'go': return 'package main\nimport "fmt"\nfunc main() {\n    fmt.Println("Hello, world!")\n}\n';
      case 'php': return '<?php\necho "Hello, world!\\n";\n';
      case 'html': return '<!DOCTYPE html>\n<html>\n<body>\n  <h1>Hello, world!</h1>\n</body>\n</html>\n';
      case 'sql': return 'SELECT "Hello, world!" AS greeting;\n';
      case 'shell':
      case 'bash': return '#!/bin/bash\necho "Hello, world!"\n';
      case 'lua': return '-- Write your Lua code here\nprint("Hello, world!")\n';
      case 'perl': return '#!/usr/bin/perl\nprint "Hello, world!\\n";\n';
      default: return `// Write your ${lang || ''} code here\n`;
    }
  };

  const handleChapterSelect = async (index) => {
    if (index === currentChapterIndex) return;
    const chapter = course.chapters_data[index];

    // Phase 40.18 — Dirty-check.  If the student has unsaved edits (autosave
    // still pending), ask before we swap the code base.
    const isDirty = (code || '') !== (savedCode || '') && (code || '').trim().length > 0;
    if (isDirty) {
      const choice = window.confirm(
        `You have unsaved changes in Chapter ${currentChapterIndex + 1}.\n\n` +
        'OK  →  Save changes and switch\nCancel  →  Discard changes and switch'
      );
      // In both cases we cancel any pending debounced save so it doesn't
      // race with the load below.
      if (codeSaveTimerRef.current) {
        clearTimeout(codeSaveTimerRef.current);
        codeSaveTimerRef.current = null;
      }
      if (choice) {
        try {
          await codeSnapshotApi.save(selectedCourse, currentChapterIndex, code);
        } catch { /* non-fatal — proceed with switch */ }
      }
    } else if (codeSaveTimerRef.current) {
      // No unsaved edits — still cancel the timer so we don't accidentally
      // overwrite the new chapter's code by the old chapter's save.
      clearTimeout(codeSaveTimerRef.current);
      codeSaveTimerRef.current = null;
    }

    setCurrentChapterIndex(index);
    // 1. Reset editor to a language-appropriate starter (never leaves stale
    //    code visible from the previous chapter).
    suppressAutosaveRef.current = true;
    const starter =
      chapter.starter_code ||
      chapter.code_template ||
      _defaultStarter(course?.language);
    setCode(starter);
    setSavedCode(starter);
    setSnapshotStatus('idle');

    // 2. Hydrate from this student's saved snapshot for (course, chapter).
    try {
      const snap = await codeSnapshotApi.get(selectedCourse, index);
      if (snap && typeof snap.code === 'string' && snap.code.length > 0) {
        suppressAutosaveRef.current = true;
        setCode(snap.code);
        setSavedCode(snap.code);
        setSnapshotStatus('saved');
      }
    } catch { /* keep starter */ }
    // Let the editor re-render before autosave arms again.
    setTimeout(() => { suppressAutosaveRef.current = false; }, 50);
    setOutput('');
    // Reset any debug state — old breakpoints reference the previous file.
    setBreakpoints([]);
    setDebugTrace({});
    setDebugSteps([]);
    setStepIdx(-1);
    if (editorRef.current) {
      editorRef.current.deltaDecorations(editorRef.current.__debugDecoIds || [], []);
      editorRef.current.__debugDecoIds = [];
    }
    
    // Update image for non-programming courses
    if (!course.has_code_lab) {
      const visuals = categoryVisuals[course.category] || categoryVisuals.life_skills;
      setChapterImage(visuals.images[index % visuals.images.length]);
      
      // Translate chapter if non-English
      if (selectedLanguage !== 'en') {
        const order = index + 1;
        const cacheKey = `${selectedCourse}_${order}_${selectedLanguage}`;
        if (translationCache[cacheKey]) {
          setCurrentChapter({ ...chapter, content: translationCache[cacheKey] });
        } else {
          setCurrentChapter(chapter); // Show English first
          const translated = await translateChapter(selectedCourse, order, selectedLanguage, chapter.content);
          setCurrentChapter(prev => ({ ...prev, content: translated }));
        }
      } else {
        setCurrentChapter(chapter);
      }
    } else {
      setCurrentChapter(chapter);
    }
  };

  const handleRunCode = async () => {
    if (!code.trim()) {
      toast.error('Please write some code first');
      return;
    }
    setActiveTab('code');
    setIsRunning(true);
    setOutput('Running...');

    // Phase 40.19 — HTML/CSS courses render a live sandboxed iframe preview
    // instead of piping through the code executor.  Students see the actual
    // rendered page just like a browser would show it.
    if (isWebPreviewLanguage) {
      setPreviewSrc(code);
      setOutput('__HTML_PREVIEW__');
      setIsRunning(false);
      toast.success('Preview rendered');
      return;
    }

    // Phase B — React Playground: transpile the JSX in-browser with Babel
    // Standalone and render inside a sandboxed iframe.  Students see their
    // real components on screen.
    if (isReactPlayground) {
      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>body{font-family:system-ui,sans-serif;padding:16px;color:#0f172a;background:#fff}</style>
    <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
    <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
    <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  </head>
  <body>
    <div id="root">Loading…</div>
    <script type="text/babel" data-type="module">
${code}

// If the student defined a top-level component called App / Counter / Timer
// / Greeting, mount that.  Otherwise leave whatever they've mounted alone.
try {
  const guess = (typeof App === 'function' && App)
    || (typeof Counter === 'function' && Counter)
    || (typeof Timer === 'function' && Timer)
    || (typeof Greeting === 'function' && Greeting)
    || (typeof Hello === 'function' && Hello);
  if (guess) {
    ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(guess));
  }
} catch (e) {
  document.getElementById('root').innerHTML =
    '<pre style="color:#b91c1c;white-space:pre-wrap">' + (e && e.message || e) + '</pre>';
}
    </script>
  </body>
</html>`;
      setPreviewSrc(html);
      setOutput('__HTML_PREVIEW__');
      setIsRunning(false);
      toast.success('React preview rendered');
      return;
    }

    try {
      const result = await executeApi.run(
        course.language,
        code,
        currentChapter?.chapter_id
      );
      
      setOutput(result.output);
      
      if (result.success) {
        toast.success(`Executed in ${result.execution_time}s`);
      }
    } catch (error) {
      setOutput(`Error: ${error.message}`);
      toast.error('Execution failed');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDebugCode = async () => {
    if (!code.trim()) return toast.error('Write some code first');
    if (breakpoints.length === 0) {
      toast.info('Click a line number in the gutter to set a breakpoint first.');
      return;
    }
    setActiveTab('code'); // Ensure the editor is visible while debugging
    setIsRunning(true);
    setOutput(`Debugging with ${breakpoints.length} breakpoint(s)...`);
    setDebugTrace({});
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await fetch(`${API}/debug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ language: course.language, code, breakpoints }),
      });
      const raw = await res.text();
      let data; try { data = JSON.parse(raw); } catch { data = { output: raw, success: false }; }
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setOutput(data.output || '(no output)');
      setDebugTrace(data.trace || {});
      const hits = (data.breakpoints_hit || []).slice().sort((a, b) => a - b);
      setDebugSteps(hits);
      setStepIdx(hits.length ? 0 : -1);
      // Refresh Monaco line highlights + hover provider.
      if (editorRef.current && monacoRef.current) {
        _installMonacoHover(editorRef.current, monacoRef.current, data.trace || {});
        _installBreakpointHighlights(editorRef.current, monacoRef.current, hits);
        if (hits.length) {
          _focusStep(editorRef.current, monacoRef.current, hits[0], data.trace || {});
        }
      }
      const hitCount = hits.length;
      if (hitCount > 0) {
        toast.success(`Paused at ${hitCount} breakpoint(s) — use Step Forward/Back or hover a line`);
      } else {
        toast.info('Debug run finished — no breakpoint was hit');
      }
    } catch (e) {
      setOutput(`Debug error: ${e.message}`);
      toast.error('Debug failed');
    } finally {
      setIsRunning(false);
    }
  };

  // Monaco helpers — Register a hover provider that reveals per-line
  // variable snapshots captured by the backend tracer, and paint breakpoint
  // hit-lines with a subtle glow so students see where execution "stopped".
  const _installMonacoHover = (editor, monaco, trace) => {
    if (hoverProviderRef.current) {
      hoverProviderRef.current.dispose();
      hoverProviderRef.current = null;
    }
    const langId = editor.getModel()?.getLanguageId() || 'plaintext';
    hoverProviderRef.current = monaco.languages.registerHoverProvider(langId, {
      provideHover: (model, position) => {
        const ln = position.lineNumber;
        const snapshot = trace[String(ln)];
        if (!snapshot || !Object.keys(snapshot).length) return null;
        const md = [
          { value: `**Variables at line ${ln}**` },
          {
            value: '```\n' + Object.entries(snapshot).map(([k, v]) => `${k} = ${v}`).join('\n') + '\n```',
          },
        ];
        return { range: new monaco.Range(ln, 1, ln, model.getLineMaxColumn(ln)), contents: md };
      },
    });
  };

  const _installBreakpointHighlights = (editor, monaco, hitLines) => {
    if (!hitLines?.length) {
      editor.deltaDecorations(editor.__debugDecoIds || [], []);
      editor.__debugDecoIds = [];
      return;
    }
    const decos = hitLines.map((line) => ({
      range: new monaco.Range(line, 1, line, 1),
      options: {
        isWholeLine: true,
        className: 'monaco-debug-line-highlight',
        linesDecorationsClassName: 'monaco-breakpoint-dot',
      },
    }));
    editor.__debugDecoIds = editor.deltaDecorations(editor.__debugDecoIds || [], decos);
  };

  // Bring the "current step" line into view with a stronger highlight.
  const _focusStep = (editor, monaco, line, trace) => {
    editor.revealLineInCenter(line);
    editor.setPosition({ lineNumber: line, column: 1 });
    // Extra "current step" decoration on top of the general highlight.
    const currentDeco = [{
      range: new monaco.Range(line, 1, line, 1),
      options: {
        isWholeLine: true,
        className: 'monaco-debug-current-line',
      },
    }];
    editor.__currentStepDecoIds = editor.deltaDecorations(editor.__currentStepDecoIds || [], currentDeco);
    // Print current variables to the terminal so the student sees them without hovering.
    const snap = (trace && trace[String(line)]) || {};
    const varStr = Object.entries(snap).map(([k, v]) => `  ${k} = ${v}`).join('\n') || '  (no variables in scope)';
    setOutput(`> [Step at line ${line}]\n${varStr}\n`);
  };

  const handleStepPrev = () => {
    if (stepIdx <= 0 || debugSteps.length === 0) return;
    const nextIdx = stepIdx - 1;
    setStepIdx(nextIdx);
    if (editorRef.current && monacoRef.current) {
      _focusStep(editorRef.current, monacoRef.current, debugSteps[nextIdx], debugTrace);
    }
  };

  const handleStepNext = () => {
    if (stepIdx >= debugSteps.length - 1 || debugSteps.length === 0) return;
    const nextIdx = stepIdx + 1;
    setStepIdx(nextIdx);
    if (editorRef.current && monacoRef.current) {
      _focusStep(editorRef.current, monacoRef.current, debugSteps[nextIdx], debugTrace);
    }
  };

  const handleContinue = () => {
    // Jump to end of the trace.
    if (!debugSteps.length) return;
    const last = debugSteps.length - 1;
    setStepIdx(last);
    if (editorRef.current && monacoRef.current) {
      _focusStep(editorRef.current, monacoRef.current, debugSteps[last], debugTrace);
    }
  };

  // Shared Monaco onMount — wires refs + gutter-click breakpoint toggle.
  // Reused across the tabs-mode editor and the two split-mode editors so
  // breakpoints work in every layout.
  const handleMonacoMount = React.useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    editor.onMouseDown((e) => {
      if (e.target?.type !== monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) return;
      const ln = e.target.position.lineNumber;
      setBreakpoints((prev) => {
        const next = prev.includes(ln) ? prev.filter((x) => x !== ln) : [...prev, ln];
        editor.createDecorationsCollection(
          next.map((line) => ({
            range: new monaco.Range(line, 1, line, 1),
            options: {
              isWholeLine: false,
              glyphMarginClassName: 'monaco-breakpoint-dot',
              glyphMarginHoverMessage: { value: `Breakpoint on line ${line}` },
            },
          }))
        );
        return next;
      });
    });
  }, []);

  const handleMarkComplete = async () => {
    if (!currentChapter) return;

    try {
      await progressApi.update(currentChapter.chapter_id, true);
      setProgress(prev => ({
        ...prev,
        completed_chapters: [...(prev.completed_chapters || []), currentChapter.chapter_id]
      }));
      toast.success('Chapter marked as complete!');
      
      // Check if this was the last chapter
      const isLastChapter = currentChapterIndex >= (course.chapters_data?.length || 1) - 1;
      
      if (isLastChapter) {
        // All chapters completed, navigate to exam
        toast.success('All chapters completed! Ready for the final exam.');
        navigate(`/exam/${courseId}`);
      } else {
        // Move to next chapter
        handleChapterSelect(currentChapterIndex + 1);
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const loadSnapshotHistory = async () => {
    if (!selectedCourse) return;
    setHistoryLoading(true);
    try {
      const res = await codeSnapshotApi.history(selectedCourse, currentChapterIndex);
      setSnapshotHistory(res?.history || []);
    } catch {
      setSnapshotHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRestoreVersion = async (idx) => {
    try {
      const res = await codeSnapshotApi.restore(selectedCourse, currentChapterIndex, idx);
      suppressAutosaveRef.current = true;
      setCode(res.code);
      setSavedCode(res.code);
      setSnapshotStatus('saved');
      toast.success('Restored earlier version');
      setTimeout(() => { suppressAutosaveRef.current = false; }, 50);
      await loadSnapshotHistory();
    } catch (e) {
      toast.error('Could not restore that version');
    }
  };

  const handleResetCode = async () => {
    if (!window.confirm('Reset this chapter to the starter code?\nYour saved edits for this chapter will be discarded (this only affects YOUR account).')) return;
    const starter = currentChapter?.starter_code || currentChapter?.code_template || _defaultStarter(course?.language);
    suppressAutosaveRef.current = true;
    setCode(starter);
    setSavedCode(starter);
    setOutput('');
    setSnapshotStatus('idle');
    try {
      await codeSnapshotApi.reset(selectedCourse, currentChapterIndex);
      toast.success('Chapter reset to starter code');
    } catch { /* non-fatal */ }
    setTimeout(() => { suppressAutosaveRef.current = false; }, 50);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isChapterCompleted = (chapterId) => {
    return progress.completed_chapters?.includes(chapterId);
  };

  const allChaptersCompleted = () => {
    if (!course?.chapters_data || !progress.completed_chapters) return false;
    return course.chapters_data.every(ch => progress.completed_chapters.includes(ch.chapter_id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading lab...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="code-lab">
      {/* Header */}
      <header className="z-10 glass border-b border-border/50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  // Phase 40.15 — return to Course Browser when CodeLab was
                  // opened from a course, else fall back to dashboard.
                  const from = new URLSearchParams(window.location.search).get('from');
                  if (from === 'courses' || document.referrer.includes('/courses')) navigate('/courses');
                  else navigate(-1);
                }}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>

              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold hidden sm:block">CognitiaIndia</span>
              </Link>

              {/* Course Dropdown */}
              <Select value={selectedCourse} onValueChange={handleCourseChange}>
                <SelectTrigger className="w-64 h-9" data-testid="course-dropdown">
                  <SelectValue placeholder="Select a course" />
                </SelectTrigger>
                <SelectContent>
                  {myCourses.map((enrollment) => (
                    <SelectItem key={enrollment.course_id} value={enrollment.course_id}>
                      {enrollment.course?.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-32 h-9" data-testid="language-selector">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.nativeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/courses')}>
                    Browse Courses
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky Top Navigation Bar */}
      <div className="sticky top-0 z-20 bg-card border-b border-border shadow-sm">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Chapter Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleChapterSelect(Math.max(0, currentChapterIndex - 1))}
                disabled={currentChapterIndex === 0}
                data-testid="prev-chapter-top-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t.previous}
              </Button>
              
              <div className="flex items-center gap-1 px-2">
                {course?.chapters_data?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleChapterSelect(index)}
                    className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                      currentChapterIndex === index
                        ? 'bg-primary text-white scale-110'
                        : isChapterCompleted(course.chapters_data[index]?.chapter_id)
                        ? 'bg-green-500 text-white'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    data-testid={`chapter-nav-${index}`}
                  >
                    {isChapterCompleted(course?.chapters_data[index]?.chapter_id) ? (
                      <CheckCircle className="w-4 h-4 mx-auto" />
                    ) : (
                      index + 1
                    )}
                  </button>
                ))}
                
                {/* Final Exam Button */}
                {course?.has_exam && (
                  <button
                    onClick={() => navigate(`/exam/${courseId}`)}
                    className="w-8 h-8 rounded-full bg-amber-500 text-white text-xs font-medium ml-2"
                    title="Final Exam"
                  >
                    <Award className="w-4 h-4 mx-auto" />
                  </button>
                )}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleChapterSelect(Math.min((course?.chapters_data?.length || 1) - 1, currentChapterIndex + 1))}
                disabled={currentChapterIndex >= (course?.chapters_data?.length || 1) - 1}
                data-testid="next-chapter-top-btn"
              >
                {t.next}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {/* Progress & Actions */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {currentChapterIndex + 1} / {course?.chapters_data?.length || 0}
              </span>
              
              {allChaptersCompleted() ? (
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600"
                  onClick={() => navigate(`/exam/${courseId}`)}
                >
                  <Award className="w-4 h-4 mr-2" />
                  {t.takeExam}
                </Button>
              ) : !isChapterCompleted(currentChapter?.chapter_id) ? (
                <Button
                  size="sm"
                  className="btn-primary"
                  onClick={handleMarkComplete}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t.markComplete}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Main Lab Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Chapters (collapsible) */}
        <div
          className={`border-r border-border bg-card flex flex-col transition-[width] duration-200 ${
            sidebarCollapsed ? 'w-10' : 'w-72'
          }`}
          data-testid="chapters-sidebar"
        >
          <div className="p-2 border-b border-border flex items-center justify-between">
            {!sidebarCollapsed && (
              <h2 className="font-semibold flex items-center gap-2 pl-2">
                <BookOpen className="w-5 h-5" />
                {t.chapters}
              </h2>
            )}
            <button
              onClick={() => setSidebarCollapsed((v) => !v)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              data-testid="sidebar-toggle-btn"
              title={sidebarCollapsed ? 'Expand chapters' : 'Collapse chapters'}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>
          {!sidebarCollapsed && (
            <ScrollArea className="flex-1">
            <div className="p-2">
              {course?.chapters_data?.map((chapter, index) => (
                <button
                  key={chapter.chapter_id}
                  onClick={() => handleChapterSelect(index)}
                  className={`w-full chapter-item rounded-lg ${
                    currentChapterIndex === index ? 'chapter-item-active' : ''
                  } ${isChapterCompleted(chapter.chapter_id) ? 'chapter-item-completed' : ''}`}
                  data-testid={`chapter-${index}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isChapterCompleted(chapter.chapter_id)
                      ? 'bg-green-500 text-white'
                      : currentChapterIndex === index
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {isChapterCompleted(chapter.chapter_id) ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className="text-sm truncate">{chapter.title}</span>
                </button>
              ))}
              
              {/* Final Exam */}
              {course?.has_exam && (
                <button
                  onClick={() => navigate(`/exam/${courseId}`)}
                  className="w-full chapter-item rounded-lg mt-2 border-t border-border pt-2 hover:bg-amber-500/10"
                  data-testid="chapter-exam"
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-amber-500 text-white">
                    <Award className="w-4 h-4" />
                  </div>
                  <span className="text-sm truncate">Final Exam</span>
                  {!allChaptersCompleted() && (
                    <span className="text-xs text-amber-500 ml-auto">Preview</span>
                  )}
                </button>
              )}
            </div>
          </ScrollArea>
          )}
        </div>

        {/* Main Content — VS Code-style tabbed panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab strip */}
          <div className="flex items-center bg-[#252526] border-b border-[#1e1e1e] text-xs text-slate-300 select-none">
            <button
              onClick={() => setActiveTab('chapter')}
              data-testid="tab-chapter"
              className={`flex items-center gap-2 px-4 py-2 border-r border-[#1e1e1e] ${
                activeTab === 'chapter'
                  ? 'bg-[#1e1e1e] text-white border-t-2 border-t-sky-500'
                  : 'text-slate-400 hover:text-white hover:bg-[#2d2d2d]'
              }`}
            >
              <FileText className="w-3.5 h-3.5 text-sky-400" />
              <span>Chapter.md</span>
            </button>
            {course?.has_code_lab !== false && (
              <button
                onClick={() => setActiveTab('concept')}
                data-testid="tab-concept"
                className={`flex items-center gap-2 px-4 py-2 border-r border-[#1e1e1e] ${
                  activeTab === 'concept'
                    ? 'bg-[#1e1e1e] text-white border-t-2 border-t-fuchsia-500'
                    : 'text-slate-400 hover:text-white hover:bg-[#2d2d2d]'
                }`}
                title="Animated concept explanation"
              >
                <Sparkles className="w-3.5 h-3.5 text-fuchsia-400" />
                <span>Concept</span>
              </button>
            )}
            {course?.has_code_lab !== false && !isSetupChapter && (
              <button
                onClick={() => setActiveTab('code')}
                data-testid="tab-code"
                className={`flex items-center gap-2 px-4 py-2 border-r border-[#1e1e1e] ${
                  activeTab === 'code'
                    ? 'bg-[#1e1e1e] text-white border-t-2 border-t-sky-500'
                    : 'text-slate-400 hover:text-white hover:bg-[#2d2d2d]'
                }`}
              >
                <FileCode className="w-3.5 h-3.5 text-amber-400" />
                <span>Codebase</span>
              </button>
            )}
            <div className="ml-auto flex items-center gap-1 pr-2">
              {isTranslating && (
                <span className="text-[10px] text-slate-500 pr-2 animate-pulse flex items-center gap-1">
                  <Globe className="w-3 h-3" /> Translating…
                </span>
              )}
              {/* Layout toggle removed — Chapter and Codebase render side-by-side */}

              {course?.has_code_lab !== false && !isSetupChapter && activeTab === 'code' && (
                <>
                  {/* Step controls — only enabled after a Debug run */}
                  {debugSteps.length > 0 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleStepPrev}
                        disabled={stepIdx <= 0}
                        data-testid="step-prev-btn"
                        className="h-7 text-slate-300 hover:text-white disabled:opacity-40"
                        title="Step Back"
                      >
                        <StepBack className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleStepNext}
                        disabled={stepIdx >= debugSteps.length - 1}
                        data-testid="step-next-btn"
                        className="h-7 text-slate-300 hover:text-white disabled:opacity-40"
                        title="Step Forward"
                      >
                        <StepForward className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleContinue}
                        disabled={stepIdx >= debugSteps.length - 1}
                        data-testid="step-continue-btn"
                        className="h-7 text-emerald-400 hover:text-emerald-300 disabled:opacity-40"
                        title="Continue to end"
                      >
                        <SkipForward className="w-3.5 h-3.5" />
                      </Button>
                      <span className="text-[10px] text-slate-500 px-1" data-testid="step-indicator">
                        {stepIdx + 1}/{debugSteps.length}
                      </span>
                    </>
                  )}

                  <DropdownMenu onOpenChange={(open) => { if (open) loadSnapshotHistory(); }}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="tabstrip-history-btn"
                        className="h-7 text-slate-300 hover:text-white"
                        title="Restore an earlier auto-saved version"
                      >
                        <History className="w-3.5 h-3.5 mr-1" />
                        History
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 bg-[#1e1e1e] border-[#333] text-slate-200">
                      <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#2a2a2a]">
                        Last 5 auto-saved versions
                      </div>
                      {historyLoading ? (
                        <div className="px-3 py-4 text-xs text-slate-400" data-testid="history-loading">Loading…</div>
                      ) : snapshotHistory.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-400" data-testid="history-empty">
                          No earlier versions yet — keep editing and they'll show up here.
                        </div>
                      ) : (
                        snapshotHistory.map((h, i) => {
                          const preview = (h.code || '').split('\n').slice(0, 2).join(' ⏎ ').slice(0, 60);
                          const when = h.saved_at ? new Date(h.saved_at).toLocaleTimeString() : '';
                          return (
                            <DropdownMenuItem
                              key={`${h.saved_at}-${i}`}
                              onClick={() => handleRestoreVersion(i)}
                              data-testid={`history-restore-${i}`}
                              className="flex flex-col items-start gap-0.5 py-2 cursor-pointer hover:bg-[#2a2a2a] focus:bg-[#2a2a2a]"
                            >
                              <div className="text-[11px] text-emerald-400">v-{snapshotHistory.length - i} · {when}</div>
                              <div className="text-[11px] text-slate-400 font-mono truncate w-full">{preview || '(empty)'}</div>
                            </DropdownMenuItem>
                          );
                        })
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetCode}
                    data-testid="tabstrip-reset-btn"
                    className="h-7 text-slate-300 hover:text-white"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    {t.reset}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDebugCode}
                    disabled={isRunning}
                    data-testid="tabstrip-debug-btn"
                    className="h-7 text-amber-400 hover:text-amber-300"
                    title={breakpoints.length ? `Debug (${breakpoints.length} breakpoints)` : 'Set a breakpoint by clicking a line-number gutter, then Debug'}
                  >
                    <Bug className="w-3.5 h-3.5 mr-1" />
                    Debug
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 bg-emerald-600 hover:bg-emerald-500 text-white"
                    onClick={handleRunCode}
                    disabled={isRunning}
                    data-testid="tabstrip-run-btn"
                  >
                    {isRunning ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Play className="w-3.5 h-3.5 mr-1" />
                    )}
                    {t.runCode}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Tab content — Concept tab overrides all layouts, otherwise use tabs/vsplit/hsplit */}
          {activeTab === 'concept' ? (
            <AnimatedConceptExplainer
              chapter={currentChapter}
              chapterIndex={currentChapterIndex}
              language={course?.language || 'python'}
            />
          ) : (<>
          {layoutMode === 'tabs' && (
          <div className="flex-1 flex flex-col overflow-hidden" data-testid="layout-tabs">
            {/* Chapter Tab */}
            {(layoutMode !== 'tabs' || activeTab === 'chapter') && (
              <div className="flex-1 overflow-hidden flex flex-col bg-background">
                <div className="p-4 border-b border-border bg-muted/30 flex items-center gap-3">
                  {/* Phase 40.18 — Pixar-style instructor avatar for non-programming courses */}
                  {course?.avatar_url && (
                    <img
                      src={course.avatar_url}
                      alt={`${course.title} instructor`}
                      data-testid="chapter-instructor-avatar"
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/40 shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    {course?.avatar_url && (
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Your instructor</div>
                    )}
                    <h3 className="font-semibold text-lg truncate">{currentChapter?.title}</h3>
                  </div>
                </div>
                <ScrollArea className="flex-1 p-6">
                  <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground prose-table:text-foreground" style={{ fontSize: '0.8125rem' }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h2: ({ node, ...props }) => (
                          <h2 style={{ marginTop: '1.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(128,128,128,0.15)' }} {...props} />
                        ),
                        h3: ({ node, ...props }) => (
                          <h3 style={{ marginTop: '1.5rem' }} {...props} />
                        ),
                      }}
                    >{currentChapter?.content || ''}</ReactMarkdown>
                    {currentChapter?.hints && currentChapter.hints.length > 0 && (
                      <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <div className="flex items-center gap-2 mb-2 text-amber-600 dark:text-amber-400">
                          <Lightbulb className="w-5 h-5" />
                          <span className="font-semibold">Hints</span>
                        </div>
                        <ul className="list-disc list-inside text-sm text-muted-foreground">
                          {currentChapter.hints.map((hint, i) => (
                            <li key={i}>{hint}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Code Tab */}
            {(layoutMode !== 'tabs' || activeTab === 'code') && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="w-full h-full flex flex-col">
                  {course?.has_code_lab !== false && !isSetupChapter && (
                    <>
            {/* Code Editor Panel OR Visual Content for non-programming */}
            <div className="w-full h-full flex flex-col">
              {course?.has_code_lab !== false ? (
                <>
                  {/* Setup Guide for Chapter 1 */}
                  {isSetupChapter ? (
                    <div className="flex-1 flex flex-col">
                      <div className="p-3 border-b border-border bg-muted/30 flex items-center gap-2">
                        <Monitor className="w-5 h-5" />
                        <span className="font-medium">{t.setupGuide}</span>
                        <span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary font-medium">
                          {course?.language?.toUpperCase()}
                        </span>
                      </div>
                      
                      <ScrollArea className="flex-1">
                        <div className="p-6 space-y-6">
                          {/* Interactive Checklist */}
                          <div className="rounded-xl border border-border">
                            <div className="bg-muted/30 px-4 py-2 flex items-center gap-2">
                              <CheckSquare className="w-5 h-5 text-green-500" />
                              <span className="font-medium">{t.checklist}</span>
                              <span className="ml-auto text-xs text-muted-foreground">
                                {Object.values(setupChecklist).filter(Boolean).length}/{currentChecklist?.length || 0} completed
                              </span>
                            </div>
                            <div className="p-4 space-y-3">
                              {currentChecklist?.map((item) => (
                                <div
                                  key={item.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                                    setupChecklist[item.id]
                                      ? 'bg-green-500/10 border-green-500/30'
                                      : 'bg-muted/30 border-border hover:border-primary/50'
                                  }`}
                                  onClick={() => toggleChecklistItem(item.id)}
                                >
                                  {setupChecklist[item.id] ? (
                                    <CheckSquare className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Square className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <span className={setupChecklist[item.id] ? 'line-through text-muted-foreground' : ''}>
                                      {item.text}
                                    </span>
                                    {item.link && (
                                      <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Download className="w-3 h-3" />
                                        Download / Visit Link
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quick Tips */}
                          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Lightbulb className="w-5 h-5 text-amber-500" />
                              <span className="font-medium text-amber-600 dark:text-amber-400">Quick Tips</span>
                            </div>
                            <ul className="space-y-2 text-sm">
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>Restart your terminal after installation</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>Run the version command to verify</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>Check the PATH environment variable if issues occur</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </ScrollArea>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-row overflow-hidden w-full">
                      {/* Monaco Editor — Debug/Run/Reset live in the outer tab-strip */}
                      <div className="flex-1 min-w-0 overflow-hidden bg-[#1e1e1e] relative" data-testid="monaco-container">
                        {/* Phase 40.19 — autosave indicator so students trust their WIP is saved */}
                        <div
                          data-testid="autosave-pill"
                          className={`absolute top-2 right-4 z-10 text-[10px] font-medium px-2 py-1 rounded-full pointer-events-none transition-opacity ${
                            snapshotStatus === 'saving'
                              ? 'bg-amber-500/20 text-amber-300 opacity-100'
                              : snapshotStatus === 'saved'
                                ? 'bg-emerald-500/20 text-emerald-300 opacity-100'
                                : 'opacity-0'
                          }`}
                        >
                          {snapshotStatus === 'saving' ? 'Saving…' : snapshotStatus === 'saved' ? 'Saved' : ''}
                        </div>
                        <Editor
                          key={`${selectedCourse}-${currentChapterIndex}`}
                          height="100%"
                          language={MONACO_LANG_MAP[course?.language] || 'plaintext'}
                          value={code}
                          onChange={handleCodeChange}
                          theme="vs-dark"
                          options={{
                            fontSize: 14,
                            fontFamily: '"JetBrains Mono", "Fira Code", Menlo, monospace',
                            minimap: { enabled: true },
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            wordWrap: 'on',
                            tabSize: 2,
                            quickSuggestions: { other: true, comments: true, strings: true },
                            suggestOnTriggerCharacters: true,
                            snippetSuggestions: 'top',
                            bracketPairColorization: { enabled: true },
                            glyphMargin: true,
                            lineNumbers: 'on',
                          }}
                          onMount={handleMonacoMount}
                        />
                      </div>

                      {/* Output panel — vertical, right-side extreme pane (Feb 2026) */}
                      <div
                        className="border-l border-[#1e1e1e] bg-[#0c0c0c] flex flex-col shrink-0 relative"
                        style={{ width: `${outputWidth}px` }}
                        data-testid="output-panel-right"
                      >
                        {/* Drag handle on the LEFT edge — students can widen/narrow */}
                        <div
                          onMouseDown={startResizeOutput}
                          data-testid="output-resize-handle"
                          title="Drag to resize the output pane"
                          className="absolute -left-1 top-0 bottom-0 w-2 cursor-col-resize z-10 hover:bg-sky-500/40 active:bg-sky-500/60"
                        />
                        <div className="px-4 py-1.5 border-b border-[#1e1e1e] flex items-center gap-4 bg-[#252526]">
                          <div className="flex items-center gap-2 text-xs text-slate-300 font-semibold">
                            <Terminal className="w-3.5 h-3.5 text-emerald-400" />
                            <span>OUTPUT</span>
                          </div>
                          {breakpoints.length > 0 && (
                            <div className="text-[10px] text-amber-400" data-testid="breakpoint-count">
                              {breakpoints.length} breakpoint{breakpoints.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                        <ScrollArea className="flex-1">
                          {output === '__HTML_PREVIEW__' ? (
                            <iframe
                              key={previewSrc}
                              data-testid="html-preview-iframe"
                              title="Web Preview"
                              sandbox="allow-scripts"
                              srcDoc={previewSrc}
                              className="w-full h-full min-h-[240px] bg-white border-0"
                            />
                          ) : (
                            <pre className="p-3 text-xs font-mono text-slate-100 whitespace-pre-wrap leading-5" data-testid="code-output">
                              {output || '> Ready.\n> Click Run to execute\n> or set breakpoints and Debug.'}
                            </pre>
                          )}
                        </ScrollArea>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Visual Content for Non-Programming Courses */
                <div className="flex-1 flex flex-col">
                  <div className="p-3 border-b border-border bg-muted/30 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    <span className="font-medium">Visual Learning</span>
                    <span className={`px-2 py-0.5 rounded text-xs text-white font-medium bg-gradient-to-r ${categoryVisuals[course?.category]?.gradient || 'from-gray-500 to-gray-600'}`}>
                      {course?.category?.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-muted/30 to-muted/10">
                    <div className="max-w-lg w-full space-y-4">
                      {/* Animated character - contextual to chapter */}
                      <ChapterCharacter
                        category={course?.category}
                        chapterTitle={currentChapter?.title}
                        chapterNum={currentChapterIndex + 1}
                        chapterContent={currentChapter?.content}
                      />
                      
                      {/* Chapter image (below character) */}
                      {chapterImage && (
                        <div className="relative rounded-xl overflow-hidden shadow-lg">
                          <img 
                            src={chapterImage} 
                            alt={currentChapter?.title}
                            className="w-full h-32 object-cover"
                            onError={(e) => e.target.parentElement.style.display = 'none'}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-2 left-3">
                            <p className="text-white text-sm font-medium">{currentChapter?.title}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Key takeaways */}
                      <div className="bg-card rounded-xl p-4 border border-border">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                          <Lightbulb className="w-4 h-4 text-amber-500" />
                          Key Takeaways
                        </h4>
                        <ul className="space-y-2 text-xs text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Read the chapter content carefully</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Think about real-life applications</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Take notes on important concepts</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Split modes — resizable panels */}
          {layoutMode === 'vsplit' && (
            <PanelGroup direction="horizontal" className="flex-1" data-testid="layout-vsplit">
              <Panel defaultSize={45} minSize={20}>
                <div className="h-full overflow-hidden flex flex-col bg-background">
                  <div className="p-3 border-b border-border bg-muted/30 flex items-center gap-3">
                    {course?.avatar_url && (
                      <img
                        src={course.avatar_url}
                        alt={`${course.title} instructor`}
                        data-testid="chapter-instructor-avatar-vsplit"
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/40 shrink-0"
                      />
                    )}
                    <h3 className="font-semibold text-base flex items-center gap-2 min-w-0 truncate">
                      <FileText className="w-4 h-4 text-sky-400 shrink-0" />
                      <span className="truncate">{currentChapter?.title}</span>
                    </h3>
                  </div>
                  <ScrollArea className="flex-1 p-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none" style={{ fontSize: '0.8125rem' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentChapter?.content || ''}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                </div>
              </Panel>
              <PanelResizeHandle className="w-1 bg-[#2d2d2d] hover:bg-sky-500 transition-colors cursor-col-resize" data-testid="resize-handle-v" />
              <Panel defaultSize={55} minSize={30}>
                <div className="h-full flex flex-col bg-[#1e1e1e]">
                  <div className="px-3 py-2 border-b border-[#1e1e1e] bg-[#252526] flex items-center gap-2 text-xs">
                    <FileCode className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-slate-200">main.{PISTON_EXT[course?.language] || 'txt'}</span>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <Editor
                      key={`${selectedCourse}-${currentChapterIndex}-vsplit`}
                      height="100%"
                      language={MONACO_LANG_MAP[course?.language] || 'plaintext'}
                      value={code}
                      onChange={handleCodeChange}
                      theme="vs-dark"
                      options={{ fontSize: 14, minimap: { enabled: true }, automaticLayout: true, glyphMargin: true, tabSize: 2 }}
                      onMount={handleMonacoMount}
                    />
                  </div>
                  <div className="h-32 border-t border-[#1e1e1e] bg-[#0c0c0c] p-2 overflow-auto">
                    <pre className="text-xs font-mono text-slate-100 whitespace-pre-wrap" data-testid="split-output">{output || '> Ready.'}</pre>
                  </div>
                </div>
              </Panel>
            </PanelGroup>
          )}

          {layoutMode === 'hsplit' && (
            <PanelGroup direction="vertical" className="flex-1" data-testid="layout-hsplit">
              <Panel defaultSize={40} minSize={15}>
                <div className="h-full overflow-hidden flex flex-col bg-background">
                  <div className="p-3 border-b border-border bg-muted/30 flex items-center gap-3">
                    {course?.avatar_url && (
                      <img
                        src={course.avatar_url}
                        alt={`${course.title} instructor`}
                        data-testid="chapter-instructor-avatar-hsplit"
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/40 shrink-0"
                      />
                    )}
                    <h3 className="font-semibold text-base flex items-center gap-2 min-w-0 truncate">
                      <FileText className="w-4 h-4 text-sky-400 shrink-0" />
                      <span className="truncate">{currentChapter?.title}</span>
                    </h3>
                  </div>
                  <ScrollArea className="flex-1 p-4">
                    <div className="prose prose-sm dark:prose-invert max-w-none" style={{ fontSize: '0.8125rem' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentChapter?.content || ''}</ReactMarkdown>
                    </div>
                  </ScrollArea>
                </div>
              </Panel>
              <PanelResizeHandle className="h-1 bg-[#2d2d2d] hover:bg-sky-500 transition-colors cursor-row-resize" data-testid="resize-handle-h" />
              <Panel defaultSize={60} minSize={30}>
                <div className="h-full flex flex-col bg-[#1e1e1e]">
                  <div className="px-3 py-2 border-b border-[#1e1e1e] bg-[#252526] flex items-center gap-2 text-xs">
                    <FileCode className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-slate-200">main.{PISTON_EXT[course?.language] || 'txt'}</span>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <Editor
                      key={`${selectedCourse}-${currentChapterIndex}-hsplit`}
                      height="100%"
                      language={MONACO_LANG_MAP[course?.language] || 'plaintext'}
                      value={code}
                      onChange={handleCodeChange}
                      theme="vs-dark"
                      options={{ fontSize: 14, minimap: { enabled: true }, automaticLayout: true, glyphMargin: true, tabSize: 2 }}
                      onMount={handleMonacoMount}
                    />
                  </div>
                  <div className="h-24 border-t border-[#1e1e1e] bg-[#0c0c0c] p-2 overflow-auto">
                    <pre className="text-xs font-mono text-slate-100 whitespace-pre-wrap" data-testid="split-output-h">{output || '> Ready.'}</pre>
                  </div>
                </div>
              </Panel>
            </PanelGroup>
          )}
          </>)}
        </div>

          {/* Bottom Status Bar - simplified */}
          <div className="p-2 border-t border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground">
            <span>{course?.title} • Chapter {currentChapterIndex + 1} of {course?.chapters_data?.length || 0}</span>
            {isChapterCompleted(currentChapter?.chapter_id) && (
              <span className="ml-2 text-green-500 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Completed
              </span>
            )}
          </div>
      </div>
      
      {/* Teacher Avatar */}
      <TeacherAvatar
        visible={showAvatar && !!currentChapter}
        avatarId={user?.avatar_id || 'male-1'}
        text={currentChapter ? `Now studying ${currentChapter.title || 'this chapter'} from ${course?.title || 'this course'}. This chapter covers key concepts that will help you build practical skills and knowledge.` : ''}
        cacheKey={currentChapter ? `codelab-${course?.course_id}-${currentChapter.chapter_id}` : ''}
        onClose={() => { setShowAvatar(false); setAvatarDismissed(true); }}
        dismissed={avatarDismissed}
        onResummon={() => { setShowAvatar(true); setAvatarDismissed(false); }}
      />
    </div>
  );
}
